<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              v-if="!payout.payout_id"
              class="title ml-2"
            >
              Create Payout
            </h3>
            <h3
              v-if="payout.payout_id"
              class="title ml-2"
            >
              Update Payout
            </h3>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              X
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Payout
                  </h6>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.description"
                    label="Description"
                    :error-messages="getFieldErrors('description')"
                    @input="$v.formData.description.$touch()"
                    @blur="$v.formData.description.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-select
                    v-model="formData.type"
                    :items="types()"
                    label="Type"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('type')"
                    @input="$v.formData.type.$touch()"
                    @blur="$v.formData.type.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-select
                    v-model="formData.tax_calculation_mode"
                    :items="taxCalculationModeTypes()"
                    label="Tax Calculation Mode"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('tax_calculation_mode')"
                    @input="$v.formData.tax_calculation_mode.$touch()"
                    @blur="$v.formData.tax_calculation_mode.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-checkbox
                    v-model="formData.is_eie"
                    label="Get Holding from EIE"
                    :error-messages="getFieldErrors('is_eie')"
                    @input="$v.formData.is_eie.$touch()"
                    @blur="$v.formData.is_eie.$touch()"
                  />
                </v-flex>
              </v-layout>

              <v-layout
                v-if="isNormalType()"
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Details
                  </h6>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-autocomplete
                    v-model="formData.security_id"
                    :items="getSecuritiesMinimal()"
                    label="Security ID"
                    :error-messages="getFieldErrors('security_id')"
                    :disabled="payout.security_id"
                    item-text="name"
                    item-value="id"
                    @input="$v.formData.security_id.$touch()"
                    @blur="$v.formData.security_id.$touch()"
                  >
                    <template
                      slot="item"
                      slot-scope="data"
                    >
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <b>{{ data.item.name }}</b> — {{ data.item.id }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.amount"
                    label="Amount (only Yield)"
                    prefix="€"
                    :error-messages="getFieldErrors('amount')"
                    @input="$v.formData.amount.$touch()"
                    @blur="$v.formData.amount.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerInterestPeriodBeginOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <div
                      style="background: white"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="error"
                        @click.stop="() => unsetPickerInterestPeriodBeginOpen()"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </div>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.interest_period_begin"
                        label="Interest Period Begin"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('interest_period_begin')"
                        v-on="on"
                        @change="$v.formData.interest_period_begin.$touch()"
                        @blur="$v.formData.interest_period_begin.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.interest_period_begin"
                      show-current
                      color="info"
                      @change="pickerInterestPeriodBeginOpen = false"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerInterestPeriodEndOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <div
                      style="background: white"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="error"
                        @click.stop="() => unsetPickerInterestPeriodEndOpen()"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </div>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.interest_period_end"
                        label="Interest Period End"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('interest_period_end')"
                        v-on="on"
                        @change="$v.formData.interest_period_end.$touch()"
                        @blur="$v.formData.interest_period_end.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.interest_period_end"
                      show-current
                      color="info"
                      @change="pickerInterestPeriodEndOpen = false"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerExpectedPayoutDateOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <div
                      style="background: white"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="error"
                        @click.stop="() => unsetPickerExpectedPayoutDateOpen()"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </div>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.expected_payout_date"
                        label="Expected Payout Date"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('expected_payout_date')"
                        v-on="on"
                        @change="$v.formData.expected_payout_date.$touch()"
                        @blur="$v.formData.expected_payout_date.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.expected_payout_date"
                      show-current
                      color="info"
                      @change="pickerExpectedPayoutDateOpen = false"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerFakePdfCreationDateOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <div
                      style="background: white"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="error"
                        @click.stop="() => unsetPickerFakePdfCreationDateOpen()"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </div>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.fake_pdf_creation_date"
                        label="Fake PDF Creation Date (Default: current Date)"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('fake_pdf_creation_date')"
                        v-on="on"
                        @change="$v.formData.fake_pdf_creation_date.$touch()"
                        @blur="$v.formData.fake_pdf_creation_date.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.fake_pdf_creation_date"
                      show-current
                      color="info"
                      @change="pickerFakePdfCreationDateOpen = false"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerCustomCumexDateOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <div
                      style="background: white"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="error"
                        @click.stop="() => unsetPickerCustomCumexDateOpen()"
                      >
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </div>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.custom_cumex_date"
                        label="Custom Cumex Date (Baader only) (Default: Interest Period End)"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('custom_cumex_date')"
                        v-on="on"
                        @change="$v.formData.custom_cumex_date.$touch()"
                        @blur="$v.formData.custom_cumex_date.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.custom_cumex_date"
                      show-current
                      color="info"
                      @change="pickerCustomCumexDateOpen = false"
                    />
                  </v-menu>
                </v-flex>
                <v-flex
                  v-if="(formData.type === 'final_maturity' || formData.type === 'final_maturity_30360' || formData.type === 'partial_interest')
                    && formData.security
                    && formData.security.type === 'financing_token'"
                  xs12
                  md6
                >
                  <PercentageInput
                    v-model="formData.interest_rate"
                    title="Interest Rate"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'delayed_interest'"
                  xs12
                  md6
                >
                  <PercentageInput
                    v-model="formData.delay_interest_rate"
                    title="Delay Interest Rate"
                  />
                </v-flex>
                <v-flex
                  v-if="isPartialInterestOfLoanType(formData)"
                  xs12
                  md6
                >
                  <v-checkbox
                    v-model="formData.set_interest_rate"
                    label="Set Interest rate"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('set_interest_rate')"
                    @input="$v.formData.set_interest_rate.$touch()"
                    @blur="$v.formData.set_interest_rate.$touch()"
                  />
                </v-flex>
                <v-flex
                  v-if="isPartialInterestOfLoanType(formData) && formData.set_interest_rate"
                  xs12
                  md6
                >
                  <PercentageInput
                    v-model="formData.interest_rate"
                    title="Interest Rate"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'delayed_interest' || formData.type === 'partial_interest'"
                  xs12
                  md6
                >
                  <PercentageInput
                    v-model="formData.interest_determination_fraction"
                    title="Interest Determination Fraction"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'partial_maturity'"
                  xs12
                  md6
                >
                  <PercentageInput
                    v-model="formData.partial_maturity_rate"
                    title="Partial Maturity Rate"
                  />
                </v-flex>
                <v-flex
                  v-if="formData.type === 'notional' || formData.type === 'notional_extra'"
                  xs12
                  md6
                >
                  <NegativePercentageInput
                    v-model="formData.notional_payback_rate"
                    title="Notional Payback Rate"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-checkbox
                    v-model="formData.import_tax_calculation"
                    label="Disable Tax Calculation & Import Data"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('import_tax_calculation')"
                    @input="$v.formData.import_tax_calculation.$touch()"
                    @blur="$v.formData.import_tax_calculation.$touch()"
                  />
                </v-flex>
              </v-layout>

              <v-layout
                v-if="isSpecialType()"
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Details
                  </h6>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.user_id"
                    label="User ID"
                    :error-messages="getFieldErrors('user_id')"
                    @input="$v.formData.user_id.$touch()"
                    @blur="$v.formData.user_id.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-autocomplete
                    v-model="formData.paying_company_id"
                    :items="getPayingCompaniesMinimal()"
                    label="Paying Company ID"
                    :error-messages="getFieldErrors('paying_company_id')"
                    :disabled="payout.paying_company_id"
                    item-text="name"
                    item-value="id"
                    @input="$v.formData.paying_company_id.$touch()"
                    @blur="$v.formData.paying_company_id.$touch()"
                  >
                    <template
                      slot="item"
                      slot-scope="data"
                    >
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <b>{{ data.item.name }}</b> — {{ data.item.id }}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.amount"
                    label="Amount (only Yield)"
                    prefix="€"
                    :error-messages="getFieldErrors('amount')"
                    @input="$v.formData.amount.$touch()"
                    @blur="$v.formData.amount.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-menu
                    v-model="pickerExpectedPayoutDateOpen"
                    :close-on-content-click="false"
                    full-width
                    max-width="290"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formData.expected_payout_date"
                        label="Expected Payout Date"
                        prepend-icon="event"
                        readonly
                        :error-messages="getFieldErrors('expected_payout_date')"
                        v-on="on"
                        @change="$v.formData.expected_payout_date.$touch()"
                        @blur="$v.formData.expected_payout_date.$touch()"
                      />
                    </template>
                    <v-date-picker
                      v-model="formData.expected_payout_date"
                      show-current
                      color="info"
                      @change="pickerExpectedPayoutDateOpen = false"
                    />
                  </v-menu>
                </v-flex>
              </v-layout>
            </v-container>
            <v-alert
              v-for="e in error"
              :key="e.path"
              :value="error"
              type="error"
              color="error"
            >
              {{ e.message }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="isLoading || !$v.formData.$anyDirty"
                  :loading="isLoading"
                  color="primary"
                  @click="submit"
                  v-on="on"
                >
                  <span v-if="!payout.payout_id">Create Payout</span>
                  <span v-if="payout.payout_id">Update Payout</span>
                </v-btn>
              </template>
              <span>Validate and submit</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import {
  between,
  decimal,
  minLength,
  required,
} from 'vuelidate/lib/validators';
import PercentageInput from '@/components/payoutsAndTaxes/payout/shared/PercentageInput';
import NegativePercentageInput from '@/components/payoutsAndTaxes/payout/shared/NegativePercentageInput';

export default {
  name: 'CreateDialog',
  components: {
    PercentageInput,
    NegativePercentageInput,
  },
  props: {
    payout: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isOpenProp: this.isOpen,
    isLoading: false,
    pickerExpectedPayoutDateOpen: false,
    pickerInterestPeriodBeginOpen: false,
    pickerInterestPeriodEndOpen: false,
    pickerFakePdfCreationDateOpen: false,
    pickerCustomCumexDateOpen: false,
    error: null,
    formData: {
      payout_id: null,
      interest_determination_fraction: 100,
      tax_calculation_mode: null,
    },
  }),
  validations() {
    return {
      formData: {
        amount: { decimal },
        description: { minLength: minLength(5), required },
        expected_payout_date: { },
        security_id: { },
        type: { required },
        tax_calculation_mode: { },
        import_tax_calculation: { },
        is_eie: { },
        set_interest_rate: { },
        interest_period_begin: { },
        interest_period_end: { },
        fake_pdf_creation_date: { },
        custom_cumex_date: { },
        delay_interest_rate: { decimal },
        interest_determination_fraction: { decimal },
        interest_rate: { decimal },
        partial_maturity_rate: { decimal, between: between(1, 99) },
        notional_payback_rate: { decimal, between: between(-100, 100) },
        user_id: { },
        paying_company_id: { },
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializePayout(this.payout)),
    };

    if (!this.payout.payout_id) {
      this.formData.tax_calculation_mode = this.taxCalculationModeTypeDefault();
    }
    if (this.payout.interest_rate) {
      this.formData.set_interest_rate = true;
    }

    this.$v.$touch();

    this.loadData();
  },
  methods: {
    loadData() {
      this.$store.dispatch('fetchPatSecurityListMinimal');
      this.$store.dispatch('fetchPatCompanyListMinimal');
    },
    getSecuritiesMinimal() {
      return this.$store.getters.getSecuritiesListMinimal;
    },
    getPayingCompaniesMinimal() {
      return this.$store.getters.getCompaniesListMinimal;
    },
    isPartialInterestOfLoanType(payout) {
      return payout.type === 'partial_interest'
                    && payout.security
                    && payout.security.type === 'loan';
    },
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return [];
      }

      return this.transformEnumToSelectArray(data.payout.types);
    },
    taxCalculationModeTypeDefault() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.taxCalculationMode || !data.payout.taxCalculationMode.default) {
        return [];
      }

      return data.payout.taxCalculationMode.default;
    },
    taxCalculationModeTypes() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.taxCalculationMode || !data.payout.taxCalculationMode.types) {
        return [];
      }

      return this.transformEnumToSelectArray(data.payout.taxCalculationMode.types);
    },
    transformEnumToSelectArray(options) {
      const newOptions = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(options)) {
        newOptions.push({
          id: key,
          name: value,
        });
      }

      return newOptions;
    },
    getFieldErrors(field) {
      const errors = [];
      const formField = this.$v.formData[field];

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (!formField.minLength) {
          errors.push('Field has a minimum length');
        }

        if (!formField.maxLength) {
          errors.push('Field has a maximum length');
        }
      }

      return errors;
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;

      this.checkAndUnsetInterestRate();

      if (this.payout.payout_id) {
        this.$store
          .dispatch('updatePayout', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch('createPayout', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializePayout(payout) {
      const { ...serializePayout } = payout;

      return {
        ...serializePayout,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;
      this.cleanObject(serializeFormData);

      return {
        ...serializeFormData,
      };
    },
    unsetPickerInterestPeriodBeginOpen() {
      this.formData.interest_period_begin = undefined;
      this.pickerInterestPeriodBeginOpen = false;
    },
    unsetPickerInterestPeriodEndOpen() {
      this.formData.interest_period_end = undefined;
      this.pickerInterestPeriodEndOpen = false;
    },
    unsetPickerExpectedPayoutDateOpen() {
      this.formData.expected_payout_date = undefined;
      this.pickerExpectedPayoutDateOpen = false;
    },
    unsetPickerFakePdfCreationDateOpen() {
      this.formData.fake_pdf_creation_date = undefined;
      this.pickerFakePdfCreationDateOpen = false;
    },
    unsetPickerCustomCumexDateOpen() {
      this.formData.custom_cumex_date = undefined;
      this.pickerCustomCumexDateOpen = false;
    },
    checkAndUnsetInterestRate() {
      if (this.isPartialInterestOfLoanType(this.formData)
          && !this.formData.set_interest_rate) {
        this.formData.interest_rate = undefined;
      }
    },
    cleanObject(obj) {
      const cleanProps = [
        'amount',
      ];
      for (const propName in obj) {
        if (cleanProps.includes(propName) && (obj[propName] === null || obj[propName] === undefined || obj[propName] === '')) {
          delete obj[propName];
        }
      }
    },
    isNormalType() {
      return [
        'delayed_interest',
        'final_maturity',
        'final_maturity_30360',
        'notional',
        'notional_extra',
        'partial_maturity',
        'quarterly_interest',
        'partial_interest',
      ].includes(this.formData.type);
    },
    isSpecialType() {
      return [
        'ex_gratia',
        'starting_balance',
        'cashback',
        'tell_a_friend',
        'cashback_v2',
      ].includes(this.formData.type);
    },
  },
};
</script>

<style lang="scss">
    .create-bridge-signing {
        .v-card__title {
            padding: 24px;
        }
        .v-card__text {
            max-height: 65vh;
            overflow: auto;
            padding-top: 32px;
        }
        .v-card__actions {
            border-top: 1px solid rgba(0, 0, 0, 0.12);
        }
        .radio-label {
            position: absolute;
            font-size: 16px;
            text-align: left;
            max-width: 133%;
            height: 20px;
            left: 12px;
            line-height: 20px;
            color: rgba(0, 0, 0, 0.54);
            -webkit-transform: translateY(-18px) scale(0.75);
            transform: translateY(0px) translateX(-6px) scale(0.75);
        }
        .subheading {
            padding: 8px;
            background: white;
            color: rgba(0, 0, 0, 0.54);
            top: -20px;
            left: 8px;
            position: absolute;
            display: flex;
            align-items: center;
        }
        .form-group {
            padding: 16px;
            margin: 0 0 24px 0;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 5px;
            position: relative;
        }
        .container.grid-list-md .layout:not(:only-child) {
            margin: 0 0 24px 0;
        }
        .v-input--selection-controls.v-input .v-label {
            text-transform: capitalize;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/payout/CreateDialog.vue